<template>
  <section>
    <p class="text-sm text-gray-500 mb-5">
      Quand le secrétariat envoie un courrier reçu, eCable cherche ces mots-clés dans son destinataire
      (Présidence, Président, Première Dame…) et prévient l'ATM par email.
    </p>

    <div v-if="loading" class="py-16 text-center text-gray-400">Chargement…</div>
    <div v-else-if="loadError" class="py-16 text-center text-red-500">{{ loadError }}</div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <!-- ============ CONFIGURATION ============ -->
      <div class="space-y-6">
        <form @submit.prevent="saveConfig" class="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <p v-if="!canEdit" class="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            Lecture seule : seul l'administrateur global peut modifier ces réglages.
          </p>

          <label class="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" v-model="form.enabled" :disabled="!canEdit" class="w-4 h-4" />
            <span class="text-sm font-semibold text-gray-700">Notification activée</span>
          </label>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Mots-clés du destinataire</label>
            <ChipsInput
              v-model="form.keywords"
              :disabled="!canEdit"
              placeholder="Ajouter un mot ou une expression, puis Entrée"
            />
            <p class="text-xs text-gray-400 mt-1">
              La casse et les accents sont ignorés, et un mot suffit à déclencher :
              « président » trouve aussi « Présidente » et « Vice-président ».
            </p>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Adresses à notifier</label>
            <ChipsInput
              v-model="form.recipients"
              :disabled="!canEdit"
              :validate="validerEmail"
              placeholder="atm@exemple.bj"
            />
            <p class="text-xs text-gray-400 mt-1">
              Vide : aucun email n'est envoyé. Ces adresses n'ont pas besoin d'être des comptes eCable.
            </p>
          </div>

          <p v-if="updatedAt" class="text-xs text-gray-400">
            Dernière modification le {{ formatDate(updatedAt) }}<span v-if="updatedBy"> par {{ updatedBy }}</span>.
          </p>

          <div v-if="canEdit" class="flex flex-wrap items-center gap-3 pt-2">
            <button type="submit" :disabled="saving || !dirty"
              class="px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">
              {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
            <button type="button" @click="sendTest" :disabled="testing || dirty || saved.recipients.length === 0"
              class="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              :title="dirty ? 'Enregistrez d\'abord vos modifications' : saved.recipients.length === 0 ? 'Enregistrez d\'abord au moins une adresse' : ''">
              <Mail class="w-4 h-4" />{{ testing ? 'Envoi…' : 'Envoyer un email de test' }}
            </button>
            <span v-if="message" class="text-emerald-600 text-sm flex items-center gap-1">
              <Check class="w-4 h-4" /> {{ message }}
            </span>
            <span v-if="error" class="text-red-500 text-sm">{{ error }}</span>
          </div>
        </form>

        <!-- ============ VÉRIFIER UN DESTINATAIRE ============ -->
        <div v-if="canEdit" class="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
          <h3 class="text-sm font-semibold text-gray-700">Vérifier un destinataire</h3>
          <p class="text-xs text-gray-400">
            Saisissez un destinataire tel qu'il apparaît sur un courrier pour voir si l'ATM serait prévenu.
            Aucun email n'est envoyé.
          </p>
          <form @submit.prevent="verifier" class="flex gap-2">
            <input v-model="aVerifier" type="text" placeholder="Ex. Cabinet de la Première Dame"
              class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" data-test="verif-input" />
            <button type="submit" :disabled="verifying || dirty || !aVerifier.trim()"
              class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              :title="dirty ? 'Enregistrez d\'abord vos modifications' : ''">
              <Search class="w-4 h-4" />Vérifier
            </button>
          </form>
          <p v-if="verdict" class="text-sm rounded-lg px-3 py-2" data-test="verdict"
            :class="verdict.concerne ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-50 text-gray-600'">
            <template v-if="verdict.concerne">
              L'ATM serait prévenu — mot-clé reconnu : {{ verdict.mots_cles.join(', ') }}.
            </template>
            <template v-else>Aucun mot-clé reconnu : l'ATM ne serait pas prévenu.</template>
          </p>
        </div>
      </div>

      <!-- ============ DERNIÈRES NOTIFICATIONS ============ -->
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">
          Dernières notifications envoyées
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                <th class="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                <th class="px-4 py-3 font-semibold">Courrier</th>
                <th class="px-4 py-3 font-semibold">Destinataire</th>
                <th class="px-4 py-3 font-semibold">Notifié</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(n, idx) in recent" :key="idx" class="border-t border-gray-100 align-top">
                <td class="px-4 py-3 text-gray-500 whitespace-nowrap">{{ formatDate(n.created_at) }}</td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <router-link :to="`/audit/courrier/${n.courrier_id}`" class="text-[#22282F] underline hover:no-underline">
                    #{{ n.courrier_id }}
                  </router-link>
                </td>
                <td class="px-4 py-3">
                  {{ n.destinataire }}
                  <span class="block text-xs text-gray-400">{{ n.mots_cles.join(', ') }}</span>
                </td>
                <td class="px-4 py-3 text-xs text-gray-600 break-all">{{ n.emails.join(', ') }}</td>
              </tr>
              <tr v-if="recent.length === 0">
                <td colspan="4" class="px-4 py-10 text-center text-gray-400">Aucune notification envoyée.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { Check, Mail, Search } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'
import ChipsInput from '@/components/notifications/ChipsInput.vue'

interface RecentNotification {
  created_at: string
  courrier_id: string
  destinataire: string | null
  mots_cles: string[]
  emails: string[]
  envoye_par: string | null
}

interface ReceptionConfig {
  enabled: boolean
  keywords: string[]
  recipients: string[]
  updated_at: string | null
  updated_by: string | null
  can_edit: boolean
  recent_notifications: RecentNotification[]
}

const URL_BASE = '/api/admin/reception-notifications'

const loading = ref(false)
const loadError = ref<string | null>(null)
const saving = ref(false)
const testing = ref(false)
const verifying = ref(false)
const message = ref<string | null>(null)
const error = ref<string | null>(null)

const canEdit = ref(false)
const updatedAt = ref<string | null>(null)
const updatedBy = ref<string | null>(null)
const recent = ref<RecentNotification[]>([])
const saved = reactive({ enabled: true, keywords: [] as string[], recipients: [] as string[] })
const form = reactive({ enabled: true, keywords: [] as string[], recipients: [] as string[] })

const aVerifier = ref('')
const verdict = ref<{ concerne: boolean; mots_cles: string[] } | null>(null)

const dirty = computed(() => JSON.stringify(form) !== JSON.stringify(saved))

function validerEmail(valeur: string): string | null {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valeur) ? null : `Adresse invalide : ${valeur}`
}

function apply(data: ReceptionConfig) {
  for (const cible of [saved, form]) {
    cible.enabled = data.enabled
    cible.keywords = [...(data.keywords || [])]
    cible.recipients = [...(data.recipients || [])]
  }
  canEdit.value = data.can_edit
  updatedAt.value = data.updated_at
  updatedBy.value = data.updated_by
  recent.value = data.recent_notifications || []
  verdict.value = null
}

function flash(text: string) {
  message.value = text
  setTimeout(() => { message.value = null }, 3000)
}

async function fetchConfig() {
  loading.value = true
  try {
    apply(await apiFetch<ReceptionConfig>(`${URL_BASE}/config/`))
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  error.value = null
  try {
    apply(await apiFetch<ReceptionConfig>(`${URL_BASE}/config/`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: form.enabled, keywords: form.keywords, recipients: form.recipients }),
    }))
    flash('Enregistré')
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

async function sendTest() {
  testing.value = true
  error.value = null
  try {
    const data = await apiFetch<{ sent_to: string[]; echecs: string[] }>(`${URL_BASE}/test/`, { method: 'POST' })
    flash(`Email de test envoyé à ${data.sent_to.join(', ')}`)
    if (data.echecs?.length) error.value = `Échec pour : ${data.echecs.join(', ')}`
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'envoi."
  } finally {
    testing.value = false
  }
}

async function verifier() {
  verifying.value = true
  error.value = null
  try {
    verdict.value = await apiFetch(`${URL_BASE}/verifier/`, {
      method: 'POST',
      body: JSON.stringify({ destinataire: aVerifier.value }),
    })
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Vérification impossible.'
  } finally {
    verifying.value = false
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(fetchConfig)
</script>
